import React, { FC, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { PropertyBox, PropertyLabel } from 'components/property-settings/property-shared-styles';
import { Box, Typography } from '@mui/material';

type ProtrusionProps = {
    handleSettings: (propertyName: string, settingValue: string) => void;
    partProtrusion: string;
    isRequired?: boolean;
};

const isValidProtrusionInput = (value: string): boolean => {
    if (/[^\d.,]/.test(value)) return false;
    const separatorCount = (value.match(/[.,]/g) ?? []).length;
    if (separatorCount > 1) return false;
    const decimalMatch = value.match(/[.,](\d+)$/);
    return !(decimalMatch && decimalMatch[1].length > 2);
};

const Protrusion: FC<ProtrusionProps> = ({ handleSettings, partProtrusion, isRequired }: ProtrusionProps) => {
    const { t: translate } = useTranslation(['construction-props']);
    const [protrusion, setProtrusion] = React.useState('');

    useEffect(() => {
        if (!partProtrusion) return;
        setProtrusion((prev) => {
            // If current value already represents the same number, keep it as typed.
            // This prevents the cycle: handleSettings sends '1.' → partProtrusion updates → useEffect overwrites '1,' with '1.'
            if (prev.replace(',', '.') === partProtrusion) return prev;
            return partProtrusion;
        });
    }, [partProtrusion]);

    const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const inputValue = event.target.value as string;
        if (!isValidProtrusionInput(inputValue)) {
            return;
        }
        setProtrusion(inputValue);
        handleSettings('protrusion', inputValue.replace(',', '.'));
    };

    return (
        <PropertyBox>
            <Box display="flex">
                <PropertyLabel variant="h5">{translate('PROTRUSION')}</PropertyLabel>
                {isRequired && (
                    <Typography
                        component="span"
                        color="error"
                        fontSize={18}
                        sx={{ marginLeft: '2px', marginTop: '-5px' }}
                    >
                        *
                    </Typography>
                )}
            </Box>

            <TextField
                fullWidth
                value={protrusion}
                label={translate('ENTER_PROTRUSION')}
                onChange={handleChange}
                inputProps={{ inputMode: 'decimal' }}
            />
        </PropertyBox>
    );
};

export default Protrusion;
